/**
 * Shared hook to load a file from S3 and return a blob URL.
 * Works for any content type — images, PDFs, etc.
 */
import { useEffect, useState } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';
import { S3Client, GetObjectCommand } from '@aws-sdk/client-s3';
import config from 'src/config';

interface UseS3BlobUrlOptions {
  /** Fallback content type if S3 response doesn't include one. Defaults to 'application/octet-stream'. */
  contentType?: string;
}

export function useS3BlobUrl (bucket: string, key: string, options?: UseS3BlobUrlOptions): string | null {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const fallbackContentType = options?.contentType ?? 'application/octet-stream';

  useEffect(() => {
    if (!bucket || !key) return;

    setBlobUrl(null);
    let cancelled = false;
    let url: string | null = null;

    const load = async () => {
      try {
        const { credentials } = await fetchAuthSession();
        if (!credentials) {
          console.warn('[useS3BlobUrl] No credentials available');
          return;
        }
        if (cancelled) return;

        const s3Client = new S3Client({ region: config.region, credentials });
        const response = await s3Client.send(new GetObjectCommand({ Bucket: bucket, Key: key }));

        if (!response.Body) {
          console.warn(`[useS3BlobUrl] Empty response body for ${key}`);
          return;
        }

        const stream = response.Body as ReadableStream;
        const reader = stream.getReader();
        const chunks: Uint8Array[] = [];
        let result = await reader.read();
        while (!result.done) {
          chunks.push(result.value as Uint8Array);
          result = await reader.read();
        }

        if (!cancelled) {
          const blob = new Blob(chunks, { type: response.ContentType ?? fallbackContentType });
          url = URL.createObjectURL(blob);
          setBlobUrl(url);
        }
      } catch (err) {
        if (!cancelled) {
          const message = err instanceof Error ? err.message : String(err);
          console.error(`[useS3BlobUrl] Failed to load ${bucket}/${key}: ${message}`);
        }
      }
    };

    load();
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [bucket, key, fallbackContentType]);

  return blobUrl;
}
